import { observer } from 'mobx-react-lite'
import { ReactElement, useRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Config } from '../../../config/ConfigManager'
import {
  BlackBackground,
  LogoImage,
  WhiteBackground,
} from '../TopNavigation/components/CommonMenuItems'
import { BannerMessage } from '../../promotion/BannerMessage'
import VehicleInfo from '../../search/AllProductsSearch/components/VehicleInfo'
import { useVehicleWidgetStore } from '../../search/VehicleSearch/store/VehicleWidgetStore'
import { VehicleWidget } from '../../search/VehicleSearch/VehicleWidget'
import { AllProductsSearch } from '../../search/AllProductsSearch/index'
import { TopNavigationBar } from '../TopNavigation/index'
import { defaultTheme } from '../../../theme/index'
import { breakpoints } from '../../../theme/breakpoints'
import { BannerAdsHeader } from '../../../ui-components/util/BannerAdsHeader'
import styled from 'styled-components'
import { HeaderProps } from './HeaderProps'
import MobileHamburgerMenu from '../TopNavigation/components/MenuItemLinks/MobileHamburgerMenu.tsx'
import { useSearchStore } from '../../../store/search/SearchStore'
import Sticky from 'react-stickynode'
import { isMobile } from 'react-device-detect'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import Navbar from '../TopNavigation/components/Navbar.tsx'
import AccountMenu from '../TopNavigation/components/accountMenu/index.tsx'
import { GfxSpecificConditionsModal } from '../../gfx/gfxSpecificConditions/GfxSpecificConditionsModal.tsx'

const Header = observer((props: HeaderProps): ReactElement => {
  const { logoUrl } = props

  const blackSectionRef = useRef<HTMLDivElement>(undefined)
  const searchStore = useSearchStore()
  const vehicleWidgetStore = useVehicleWidgetStore()
  const { showGfxConditions } = useUiStateStore()
  const location = useLocation()
  const isSticky =
    location.pathname === '/searchResults' || location.pathname === '/product/'

  const stickySectionRef = useRef<HTMLDivElement>(null)

  return (
    <>
      {Config.bannerEnabled && <BannerMessage />}
      {showGfxConditions && <GfxSpecificConditionsModal />}

      <StickyInnerWrapper ref={stickySectionRef}>
        <TopNavigationBar>
          <BlackBackground ref={blackSectionRef}>
            <MobileHamburgerMenu />
            <NavLink to="/home">
              <LogoImage src={logoUrl} alt="My place 4 parts logo" />
            </NavLink>
            <NavbarWrapper>
              <Navbar />
            </NavbarWrapper>
          </BlackBackground>
          <WhiteBackground>
            <AccountMenu />
          </WhiteBackground>
        </TopNavigationBar>
        <Sticky innerZ={100} enabled={isMobile && isSticky}>
          <VehicleInfoSection>
            <VehicleInfoWrapper>
              <VehicleInfo
                vehicle={searchStore.currentVehicle}
                open={vehicleWidgetStore.isDrawerOpened}
                onClick={() => {
                  vehicleWidgetStore.setIsDrawerOpened(
                    !vehicleWidgetStore.isDrawerOpened
                  )
                }}
              />
            </VehicleInfoWrapper>
          </VehicleInfoSection>
        </Sticky>
      </StickyInnerWrapper>
      <SearchContainer isSticky={isSticky}>
        <AllProductsSearchContainer>
          <AllProductsSearch />
        </AllProductsSearchContainer>
        <VehicleWidgetContainer>
          <VehicleWidget isSticky={isSticky} />
        </VehicleWidgetContainer>
      </SearchContainer>
      {import.meta.env.VITE_FEATURE_BANNER_ADS_HEADER === 'true' && (
        <BannerAdsHeader />
      )}
    </>
  )
})

interface isStickyProp {
  isSticky: boolean
}

const StickyInnerWrapper = styled.div``

const VehicleInfoSection = styled.div`
  position: relative;
  @media screen and (${breakpoints.tabletLarge}px <= width) {
    display: none;
  }
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    width: 100%;
    padding: 16px 16px 8px 16px;
    background: ${defaultTheme.colors.componentsBackground};
  }
`

const VehicleInfoWrapper = styled.div`
  background: ${defaultTheme.colors.rowFeaturedBackground};
`

const SearchContainer = styled.div<isStickyProp>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: clip;
  position: ${(p) => (p.isSticky ? 'sticky' : 'initial')};
  top: 0;
  z-index: ${(p) => (p.isSticky ? 2 : 1)};
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    position: initial;
  }
`

const AllProductsSearchContainer = styled.div`
  order: 1;
  width: 100%;
  min-width: 0;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    order: 2;
  }
`

const VehicleWidgetContainer = styled.div`
  background: ${defaultTheme.colors.componentsBackground};
  order: 2;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    order: 1;
    padding: 8px 0px 0px 0px;
  }
`

const NavbarWrapper = styled.div`
  width: 100%;
  padding: 0px 0px 0px 32px;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: none;
  }
`

export default Header
